<template>
    <WebSocketLoadingModal
        :title="__('tools.testing.snapshots.compare.pending_modal.notice')"
        :show-modal="showModal"
        :soft-timeout="softTimeout"
        :status-hint="statusHint"
        :dismiss-note="__('tools.testing.snapshots.status.not_cancellable')"
        @close="closeModal"
    />
</template>

<script setup>
import { __ } from '~@/Composables/translation'
import WebSocketLoadingModal from '~@/Components/Common/Modals/WebSocketLoadingModal'
import { usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { onUnmounted } from 'vue'

const DONE_EVENT = '.tenant.snapshot.comparison_done'
const FAILED_EVENT = '.tenant.snapshot.comparison_failed'

defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    softTimeout: {
        type: Number,
        default: null
    },
    // Passed down from Index.vue, which owns the single status poller for the page.
    statusHint: {
        type: String,
        default: null
    }
})

const emit = defineEmits(['close', 'comparisonDone', 'comparisonFailed'])

const closeModal = () => {
    emit('close')
}

const channel = window.laravelEcho.private(`onecrm.tenant.snapshot.${usePage().props.auth.user.id}`)

const onComparisonDone = ({ snapshot, workflowId }) => {
    window.axios
        .get(route('tools.snapshot.comparison-results', { workflowId }))
        .then(({ data }) => {
            // Passed whole rather than spread across positional arguments: the payload has grown to six fields
            // and the two that describe truncation only make sense next to the list they describe.
            emit('comparisonDone', snapshot, data)
        })
        .catch(() => {
            emit('comparisonFailed', __('tools.testing.snapshots.compare.results_not_found'))
        })
}

const onComparisonFailed = ({ error }) => {
    emit('comparisonFailed', error)
}

channel
    .listen(DONE_EVENT, onComparisonDone)
    .listen(FAILED_EVENT, onComparisonFailed)

// This component sits behind a v-if, so without this every comparison - and every comparison that was refused before
// it even started - would leave another pair of listeners on the channel. For this one that also means one result
// fetch per stacked listener.
onUnmounted(() => {
    channel.stopListening(DONE_EVENT, onComparisonDone)
    channel.stopListening(FAILED_EVENT, onComparisonFailed)
})

</script>
